import React, { useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { IconCalendar } from 'components/Icons'
import SkeletonLiveCard from 'components/Skeleton/SkeletonLiveCard'
import SkeletonBroadcastWeekDays from 'components/Skeleton/SkeletonBroadcastWeekDays'
import { BROADCAST_LIST, SELECTED_BROADCAST_DATE, currentDate } from 'core/store/broadcastTable'
import TodayBroadcastList from './TodayBroadcastList'
import PrevNextBroadcastList from './PrevNextBroadcastList'
import BroadcastWeekDays from './BroadcastWeekDays'
import EmptyBroadcastList from './EmptyBroadcastList'
import { useTranslation } from 'react-i18next'
import { requestBridge } from 'core/util/default'
import { BRIDGE } from 'core/constants/bridge'
import { getUA } from 'core/singleton'

const BroadcastTable: React.FC = () => {
  const selectedDate = useRecoilValue(SELECTED_BROADCAST_DATE)
  const broadcastList = useRecoilValue(BROADCAST_LIST(selectedDate))
  const [isEmpty, setIsEmpty] = useState<boolean>(false)
  const isToday = currentDate.getDate() === selectedDate.getDate()
  const { t } = useTranslation()
  const { isSDK } = getUA()

  const ref = useRef<HTMLDivElement>(null)
  const layoutEl = ref.current

  useEffect(() => {
    const layoutEl = ref.current
    if (!layoutEl || !isSDK) return

    const bridgeTimer = setTimeout(() => {
      requestBridge(BRIDGE.DOM_RECT, {
        domRect: layoutEl.getBoundingClientRect(),
        elementId: 'broadcastTable',
      })
    }, 0.5 * 100)

    return () => {
      clearTimeout(bridgeTimer)
    }
  }, [selectedDate, broadcastList.fromNow.length, isSDK])

  return (
    <StyledLayout ref={ref}>
      <StyledHeader>
        <StyledTitle>
          <IconCalendar />
          {t('broadcast_table')}
        </StyledTitle>
        <React.Suspense fallback={<SkeletonBroadcastWeekDays />}>
          <BroadcastWeekDays setIsEmpty={setIsEmpty} />
        </React.Suspense>
        <StyledTableContainer>
          <React.Suspense fallback={<SkeletonLiveCard layoutEl={layoutEl} />}>
            {isToday && (
              <>
                {isEmpty && <EmptyBroadcastList />}
                {!isEmpty && <TodayBroadcastList layoutEl={layoutEl} />}
              </>
            )}
            {!isToday && <PrevNextBroadcastList />}
          </React.Suspense>
        </StyledTableContainer>
      </StyledHeader>
    </StyledLayout>
  )
}

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 10px;
  padding-right: 10px;
`

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledTitle = styled.p`
  display: flex;
  align-items: center;
  height: 68px;
  color: #000;
  font-size: 20px;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 32px;
`

const StyledTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 59px;
  max-width: 930px;
  width: 100%;
  margin: 0 auto;
  @media screen and (max-width: 768px) {
    gap: 20px;
    padding-left: 4px;
    padding-right: 4px;
  }
`

export default BroadcastTable
